/**
 * @module components/ErrorBoundary/ErrorBoundary
 * @description Error Boundary de React para aislar fallos por módulo.
 *
 * Captura errores de JavaScript en el árbol de componentes hijo y muestra
 * una UI de respaldo en lugar de crashear toda la aplicación. Cada provider
 * en `App.tsx` está envuelto en su propio `ErrorBoundary` con un `boundaryName`
 * descriptivo, de modo que un fallo en el módulo ACS no afecta al módulo ISO 27002.
 *
 * Características:
 * - Captura errores durante el renderizado
 * - Captura errores en métodos de ciclo de vida
 * - Captura errores en constructores
 * - Permite personalizar la UI de respaldo
 * - Permite ejecutar callbacks cuando ocurre un error
 * - Botón para recargar la aplicación
 *
 * Uso:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 *
 * @author Sistema ISO 27001/27002
 * @version 1.0.0
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

interface Props {
  /** Componentes hijos a renderizar */
  children: ReactNode;
  /** UI personalizada para mostrar cuando hay error */
  fallback?: ReactNode;
  /** Callback que se ejecuta cuando ocurre un error */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Nombre del boundary para identificar en logs */
  boundaryName?: string;
}

interface State {
  /** Indica si hay un error activo */
  hasError: boolean;
  /** El error capturado */
  error: Error | null;
  /** Información adicional del error */
  errorInfo: ErrorInfo | null;
}

// =====================================================
// ERROR BOUNDARY CLASS COMPONENT
// =====================================================

/**
 * Error Boundary para capturar y manejar errores de React
 *
 * IMPORTANTE: Debe ser un class component porque los hooks no soportan
 * getDerivedStateFromError ni componentDidCatch
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Actualiza el estado cuando se captura un error
   * Este método se llama durante la fase de renderizado
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Se ejecuta después de que un error ha sido capturado
   * Este método se llama durante la fase de commit
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Guardar información del error en el estado
    this.setState({
      errorInfo,
    });

    // Registrar error en consola (en desarrollo)
    console.error('❌ [ErrorBoundary] Error capturado:', {
      boundary: this.props.boundaryName || 'Unnamed',
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Ejecutar callback personalizado si existe
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (callbackError) {
        console.error('❌ [ErrorBoundary] Error en callback onError:', callbackError);
      }
    }

    // TODO: Aquí se podría enviar el error a un servicio de tracking
    // como Sentry, LogRocket, etc.
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  /**
   * Resetea el error boundary para intentar renderizar de nuevo
   */
  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Recarga la página completamente
   */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Si hay fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de error por defecto
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            {/* Icono y título */}
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Algo salió mal
              </h1>
            </div>

            {/* Mensaje de error */}
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Ha ocurrido un error inesperado en la aplicación. Por favor, intenta recargar la página.
            </p>

            {/* Detalles del error (solo en desarrollo) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                <summary className="cursor-pointer text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Detalles del error (solo en desarrollo)
                </summary>
                <div className="text-xs font-mono text-red-700 dark:text-red-300 space-y-2">
                  <div>
                    <strong>Error:</strong>
                    <p className="mt-1 whitespace-pre-wrap break-all">
                      {this.state.error.toString()}
                    </p>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 overflow-auto max-h-40 text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Acciones */}
            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleReload}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar página
              </button>

              {import.meta.env.DEV && (
                <button
                  onClick={this.resetErrorBoundary}
                  className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Intentar recuperar (Dev)
                </button>
              )}
            </div>

            {/* Información adicional */}
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
              {this.props.boundaryName && (
                <span>Boundary: {this.props.boundaryName} · </span>
              )}
              Si el problema persiste, contacta a soporte técnico.
            </p>
          </div>
        </div>
      );
    }

    // No hay error, renderizar hijos normalmente
    return this.props.children;
  }
}

// =====================================================
// COMPONENTE FUNCIONAL WRAPPER (para usar con hooks)
// =====================================================

/**
 * Wrapper funcional del Error Boundary para uso más conveniente
 */
interface ErrorBoundaryWrapperProps extends Props {
  /** Contenido a renderizar si no hay error */
  children: ReactNode;
}

export function ErrorBoundaryWrapper({
  children,
  fallback,
  onError,
  boundaryName,
}: ErrorBoundaryWrapperProps) {
  return (
    <ErrorBoundary
      fallback={fallback}
      onError={onError}
      boundaryName={boundaryName}
    >
      {children}
    </ErrorBoundary>
  );
}

// =====================================================
// EXPORTACIONES
// =====================================================

export default ErrorBoundary;
